import type { ApplicationApi } from "./applicationApi";

export interface SageSettingsResponse {
  applicationVersion: string;
  connection: {
    companyId: string;
    environment: string;
    lastSuccessfulApiRequestAt: string | null;
    lastSuccessfulAuthenticationAt: string | null;
    lastSuccessfulSyncAt: string | null;
    status: string;
  };
  currentContext: {
    locationId: string | null;
    userId: string;
  };
  protocolVersion: number;
  recordTypes: Array<{
    available: boolean;
    id: string;
    label: string;
    unavailableReason: string | null;
  }>;
  runs: SageSyncRun[];
}

export interface SageSyncRun {
  attemptNumber: number;
  completedAt: string | null;
  completedItems: number;
  createdAt: string;
  failureCount: number;
  id: string;
  recordsAdded: number;
  recordsDeactivated: number;
  recordsUpdated: number;
  recordTypes: string[];
  retryOfSyncRunId: string | null;
  scope: string;
  status: "queued" | "running" | "succeeded" | "partial" | "failed";
  totalItems: number;
  warningCount: number;
}

export interface SageSyncResponse {
  correlationId: string;
  failureCount: number;
  recordsAdded: number;
  recordsDeactivated: number;
  recordsUpdated: number;
  runId: string;
  status: "queued" | "running" | "succeeded" | "partial" | "failed";
  warningCount: number;
}

export function getSageSettings(api: ApplicationApi): Promise<SageSettingsResponse> {
  return api.get<SageSettingsResponse>("sage-master-data");
}

export function getActiveSageSyncRun(runs: SageSyncRun[]): SageSyncRun | null {
  return runs.find((run) => run.status === "queued" || run.status === "running") ?? null;
}

export function isActiveSageSyncStatus(status: SageSyncResponse["status"]): boolean {
  return status === "queued" || status === "running";
}

export function startSageMasterDataSync(api: ApplicationApi): Promise<SageSyncResponse> {
  return api.post<SageSyncResponse, { command: "start"; recordTypes: string[] }>("sage-master-data", {
    command: "start",
    recordTypes: [
      "gl_account", "journal", "entity", "location", "department", "class",
      "project", "vendor", "customer", "employee", "item", "user_defined",
    ],
  });
}

export function advanceSageMasterDataSync(api: ApplicationApi, runId: string): Promise<SageSyncResponse> {
  return api.post<SageSyncResponse, { command: "advance"; runId: string }>("sage-master-data", {
    command: "advance",
    runId,
  });
}
